/**
 * Worker Loading Indicator
 * Shows at bottom center of canvas when worker operations are running
 */
import { useEffect, useState } from 'react';
import useEditorStore from '../stores/useEditorStore';

export default function WorkerLoadingIndicator() {
  const isWorkerBusy = useEditorStore(state => state.isWorkerBusy);
  const workerProgress = useEditorStore(state => state.workerProgress);
  const [visible, setVisible] = useState(false);

  // Component mount log
  useEffect(() => {
    console.log('[WorkerLoadingIndicator] Component MOUNTED');
    return () => console.log('[WorkerLoadingIndicator] Component UNMOUNTED');
  }, []);

  // Log every state change
  useEffect(() => {
    console.log('[WorkerLoadingIndicator] State changed - isWorkerBusy:', isWorkerBusy, 'workerProgress:', workerProgress);
  }, [isWorkerBusy, workerProgress]);

  // Fade in/out animation
  useEffect(() => {
    if (isWorkerBusy) {
      console.log('[WorkerLoadingIndicator] Worker busy, showing indicator. Progress:', workerProgress);
      setVisible(true);
    } else {
      console.log('[WorkerLoadingIndicator] Worker idle, hiding indicator');
      // Delay hiding to allow fade out animation
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isWorkerBusy, workerProgress]);

  if (!visible && !isWorkerBusy) return null;

  const operationText = workerProgress?.operation || 'Processing';
  const progressPercent = workerProgress?.progress;

  console.log('[WorkerLoadingIndicator] Rendering. Visible:', visible, 'Busy:', isWorkerBusy, 'Text:', operationText);

  return (
    <div
      className={`
        absolute bottom-8 left-1/2 -translate-x-1/2
        flex items-center gap-3 px-4 py-2.5
        bg-white/90
        rounded-full shadow-lg border-2 border-purple-200
        transition-opacity duration-300 z-[500]
        ${isWorkerBusy ? 'opacity-100' : 'opacity-0'}
      `}
      style={{ pointerEvents: 'none' }}
    >
      {/* Spinner */}
      <div className="relative w-5 h-5">
        <div className="absolute inset-0 border-2 border-gray-200 rounded-full"></div>
        <div className="absolute inset-0 border-2 border-t-purple-600 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
      </div>

      {/* Text */}
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-700">
          {operationText}
        </span>
        {progressPercent !== null && progressPercent !== undefined && (
          <span className="text-xs text-gray-500">
            {progressPercent}% complete
          </span>
        )}
      </div>

      {/* Progress bar (if progress provided) */}
      {progressPercent !== null && progressPercent !== undefined && (
        <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-600 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
}
